/**
 * Cascade workbench — the editable schedule-change preview.
 *
 * Hosts feed it the change under review (a source date push and/or staged
 * dependency edits); the workbench shows every activity the forward pass
 * moves, in cascade order, and lets the user stage RECOVERY edits right in
 * the list — shorten durations, retype dependency type/lag — re-running the
 * simulation live after each edit. The critical-path banner and impacted
 * count update with every recompute (and flip to "restored" when the edits
 * absorb the push). Save opens a confirmation summary; the host applies the
 * whole batch in one call (one batch_id = one-click undo).
 *
 * Read-only mode (banner re-open): pass readOnly + presetResult.
 */
import React from "react";
import { searchActivities, workbenchPreview } from "../../../../api/clientV2";
import { apiErrorMessage } from "../../../../utils/apiError";
import { formatDateMonthDay, formatSignedDays } from "../../../../utils/format";
import type {
  DepEditOp,
  ExternalCandidate,
  TaktColorEntry,
  WorkbenchBody,
  WorkbenchLogicEdit,
  WorkbenchPred,
  WorkbenchResult,
  WorkbenchRow,
  WorkbenchSourceInput,
} from "../../../../models";
import { formatLagSuffix, predTypeLabel } from "../../../../utils/p6";
import type { PredType } from "../../../../utils/p6";
import WorkbenchRows, { type WorkbenchRowsHandle } from "./WorkbenchRows/WorkbenchRows";
import Spinner from "../../../../components/Spinner/Spinner";
import WorkbenchRail from "./WorkbenchRail/WorkbenchRail";
import ConfirmSummaryModal from "./ConfirmSummaryModal/ConfirmSummaryModal";
import DateDelta from "./DateDelta/DateDelta";
import "./CascadeWorkbenchModal.css";

export interface RecoveryDesc {
  kind: "dep" | "dur";
  label: string;
  was: string;
  now: string;
}

/** The minimal row identity the edge-edit handlers need for rail labels —
 *  satisfied by WorkbenchRow and WorkbenchSource alike. */
export type WorkbenchRowLike = Pick<WorkbenchRow, "activity_id" | "task_code" | "task_name">;

const relLabel = (type: PredType, lagHours: number) =>
  `${predTypeLabel(type)}${formatLagSuffix(lagHours)}`;

/** Ops with any staged update/remove for this edge dropped. */
const withoutEdge = (ops: DepEditOp[], edgeId: number) =>
  ops.filter((op) => !("edge_id" in op) || op.edge_id !== edgeId);

interface Props {
  scheduleId: number;
  /** The change under review. Hosts must pass referentially-stable values
   *  (created once when opening the workbench). */
  source?: WorkbenchSourceInput | null;
  initialLogicEdits?: WorkbenchLogicEdit[];
  /** "Save all": replan these activities (+ downstream) even with no edit, so a
   *  section pulled forward by a data-date advance commits. Stable reference. */
  replanScopeIds?: number[];
  /** F9: recompute the whole schedule from the data date (no source, no scope). */
  replanAll?: boolean;
  /** F9 Simulate: restate in-progress active work against the data date first. */
  applyActuals?: boolean;
  /** F9 Simulate: duration overrides from the Auto Actuals tab (activity_id ->
   *  days), seeded into the workbench so they preview/apply with the cascade.
   *  Stable reference (created once when opening). */
  initialDurationEdits?: Map<number, number>;
  /** Header line for no-source flows (dep editor): "MEP — 3 dependency edits". */
  headerLabel?: string;
  colors: Record<string, TaktColorEntry>;
  activityWorkCodeMap: Map<number, string>;
  sectionMap: Map<number, string>;
  zoneMap: Map<number, string>;
  readOnly?: boolean;
  /** Render this result without fetching (read-only banner re-open). */
  presetResult?: WorkbenchResult;
  /** Simulate flow: when set, the footer shows "Back" (returns to the prior
   *  apply-actuals step) instead of "Cancel"; the header ✕ still closes. */
  onBack?: () => void;
  onCancel: () => void;
  /** Host owns the apply call (survey records its response first, then
   *  applies with survey_id). Required unless readOnly. */
  onApply?: (body: WorkbenchBody) => Promise<WorkbenchResult>;
  onApplied?: (result: WorkbenchResult) => void;
}

const EMPTY_LOGIC_EDITS: WorkbenchLogicEdit[] = [];

/** A footer apply action with a small explanatory note under it, so the
 *  buttons (Cancel / Apply Edits Only / Save All) top-align with their notes
 *  hanging below. */
const ActionCol: React.FC<{
  btnClass: string;
  onClick: () => void;
  disabled: boolean;
  title?: string;
  note: string;
  children: React.ReactNode;
}> = ({ btnClass, onClick, disabled, title, note, children }) => (
  <div className="cwb-action-col">
    <button className={`cwb-btn ${btnClass}`} onClick={onClick} disabled={disabled} title={title}>
      {children}
    </button>
    <span className="cwb-action-note">{note}</span>
  </div>
);

const CascadeWorkbenchModal: React.FC<Props> = ({
  scheduleId,
  source = null,
  initialLogicEdits = EMPTY_LOGIC_EDITS,
  replanScopeIds,
  replanAll = false,
  applyActuals = false,
  initialDurationEdits,
  headerLabel,
  colors,
  activityWorkCodeMap,
  sectionMap,
  zoneMap,
  readOnly = false,
  presetResult,
  onBack,
  onCancel,
  onApply,
  onApplied,
}) => {
  const [result, setResult] = React.useState<WorkbenchResult | null>(presetResult ?? null);
  const [previewing, setPreviewing] = React.useState(!readOnly);
  const [applying, setApplying] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [showConfirm, setShowConfirm] = React.useState(false);

  // Recovery edits staged in this workbench session (on top of the change
  // under review). Ops carry committed edge ids / activity ids; descs render
  // in the rail + confirmation summary, keyed "e<edge_id>" / "d<activity_id>".
  const [recoveryOps, setRecoveryOps] = React.useState<DepEditOp[]>([]);
  const [durationEdits, setDurationEdits] = React.useState<Map<number, number>>(
    () => new Map(initialDurationEdits ?? []),
  );
  const [recoveryDescs, setRecoveryDescs] = React.useState<Map<string, RecoveryDesc>>(new Map());
  // Re-pointed predecessors (search the schedule, pick a different activity):
  // old edge id -> the staged "add" of the new tie. Committed as remove(old) +
  // add(new); kept apart from recoveryOps because the add carries no edge id.
  const [repoints, setRepoints] = React.useState<Map<number, DepEditOp>>(new Map());
  // "Leave floating" dismissal for the floating-date advisory (per session).
  const [floatDismissed, setFloatDismissed] = React.useState(false);

  // Pre-edit values, captured the first time an edge/duration is staged so a
  // value typed back to the original unstages cleanly.
  const originalRels = React.useRef<Map<number, { type: PredType; lagHours: number }>>(new Map());
  const originalDurations = React.useRef<Map<number, number>>(new Map());

  // Baseline from the FIRST preview — the live deltas ("▼ 3 recovered",
  // "critical path restored") are measured against the unrecovered impact.
  const [baseline, setBaseline] = React.useState<{
    impacted: number;
    cpImpact: boolean;
  } | null>(
    presetResult
      ? {
          impacted: presetResult.summary.impacted,
          cpImpact: presetResult.summary.critical_path_impact,
        }
      : null,
  );

  const buildBody = React.useCallback((): WorkbenchBody => ({
    source,
    logic_edits: [
      ...initialLogicEdits,
      ...recoveryOps,
      ...Array.from(repoints.entries()).flatMap(
        ([oldEdgeId, addOp]): DepEditOp[] => [{ op: "remove", edge_id: oldEdgeId }, addOp],
      ),
    ],
    duration_edits: Array.from(durationEdits, ([activity_id, duration_days]) => ({
      activity_id,
      duration_days,
    })),
    ...(replanScopeIds && replanScopeIds.length > 0
      ? { replan_scope_ids: replanScopeIds }
      : {}),
    ...(replanAll ? { replan_all: true } : {}),
    ...(applyActuals ? { apply_actuals: true } : {}),
  }), [source, initialLogicEdits, recoveryOps, repoints, durationEdits, replanScopeIds, replanAll, applyActuals]);

  // Live preview loop — immediate on open, debounced 250ms per staged edit.
  const fetchSeqRef = React.useRef(0);
  const hasResultRef = React.useRef(presetResult != null);
  React.useEffect(() => {
    if (readOnly) return;
    const seq = ++fetchSeqRef.current;
    const run = async () => {
      setPreviewing(true);
      try {
        const r = await workbenchPreview(scheduleId, buildBody());
        if (seq !== fetchSeqRef.current) return;
        setResult(r);
        hasResultRef.current = true;
        setError(r.ok ? null : r.error || "Some edits failed validation");
        if (r.ok) {
          setBaseline((prev) =>
            prev ?? {
              impacted: r.summary.impacted,
              cpImpact: r.summary.critical_path_impact,
            },
          );
        }
      } catch (err) {
        if (seq !== fetchSeqRef.current) return;
        setError(apiErrorMessage(err, "Preview failed"));
      } finally {
        if (seq === fetchSeqRef.current) setPreviewing(false);
      }
    };
    if (!hasResultRef.current) {
      void run();
      return;
    }
    // Pending-debounce counts as previewing so Save All can't open the
    // confirmation against numbers a staged edit is about to change.
    setPreviewing(true);
    const t = window.setTimeout(() => void run(), 250);
    return () => window.clearTimeout(t);
  }, [scheduleId, readOnly, buildBody]);

  // ---- recovery staging -----------------------------------------------------

  const setDesc = React.useCallback((key: string, desc: RecoveryDesc | null) => {
    setRecoveryDescs((prev) => {
      const next = new Map(prev);
      if (desc == null) next.delete(key);
      else next.set(key, desc);
      return next;
    });
  }, []);

  /** The edge's pre-edit relation, captured the first time it's staged. */
  const captureRel = React.useCallback(
    (edgeId: number, type: PredType, lagHours: number) => {
      if (!originalRels.current.has(edgeId)) {
        originalRels.current.set(edgeId, { type, lagHours });
      }
      return originalRels.current.get(edgeId)!;
    },
    [],
  );

  /** Retype/re-lag a committed edge. patch carries whichever of type/lag the
   *  chip committed; merging with any already-staged update happens here. */
  const stageEdgeChange = React.useCallback(
    (row: WorkbenchRowLike, pred: WorkbenchPred, patch: { type?: PredType; lagDays?: number }) => {
      if (pred.edge_id == null) return;
      const edgeId = pred.edge_id;
      const orig = captureRel(edgeId, pred.pred_type as PredType, pred.lag_hours);
      setRecoveryOps((prev) => {
        const existing = prev.find(
          (op) => op.op === "update" && op.edge_id === edgeId,
        ) as Extract<DepEditOp, { op: "update" }> | undefined;
        const cur = existing
          ? { type: existing.pred_type as PredType, lagHours: existing.lag_hours }
          : { type: pred.pred_type as PredType, lagHours: pred.lag_hours };
        const next = {
          type: patch.type ?? cur.type,
          lagHours: patch.lagDays != null ? patch.lagDays * 8 : cur.lagHours,
        };
        if (next.type === orig.type && next.lagHours === orig.lagHours) {
          // Back to (or still at) the original: unstage. Identity-stable when
          // nothing was staged, so a no-op commit doesn't refetch.
          if (!existing) return prev;
          setDesc(`e${edgeId}`, null);
          return withoutEdge(prev, edgeId);
        }
        setDesc(`e${edgeId}`, {
          kind: "dep",
          label: `${row.task_code} ${row.task_name} ← ${pred.pred_task_code}`,
          was: relLabel(orig.type, orig.lagHours),
          now: relLabel(next.type, next.lagHours),
        });
        return [
          ...withoutEdge(prev, edgeId),
          { op: "update", edge_id: edgeId, pred_type: next.type, lag_hours: next.lagHours },
        ];
      });
    },
    [captureRel, setDesc],
  );

  const stageEdgeRemove = React.useCallback(
    (row: WorkbenchRowLike, pred: WorkbenchPred) => {
      if (pred.edge_id == null) return;
      const edgeId = pred.edge_id;
      const orig = captureRel(edgeId, pred.pred_type as PredType, pred.lag_hours);
      setRecoveryOps((prev) => [
        ...withoutEdge(prev, edgeId),
        { op: "remove", edge_id: edgeId },
      ]);
      setDesc(`e${edgeId}`, {
        kind: "dep",
        label: `${row.task_code} ${row.task_name} ← ${pred.pred_task_code}`,
        was: relLabel(orig.type, orig.lagHours),
        now: "removed",
      });
    },
    [captureRel, setDesc],
  );

  // Re-point a predecessor at a different activity picked from the schedule
  // search. Modeled as remove(old edge) + add(new tie) keeping the same
  // relationship (type + lag), under the old edge's rail key so one ✕ undoes it.
  const stageEdgeReplace = React.useCallback(
    (row: WorkbenchRowLike, pred: WorkbenchPred, external: ExternalCandidate) => {
      if (pred.edge_id == null) return;
      const edgeId = pred.edge_id;
      const orig = captureRel(edgeId, pred.pred_type as PredType, pred.lag_hours);
      const addOp: DepEditOp = {
        op: "add",
        pred_activity_id: external.externalActivityId,
        succ_activity_id: row.activity_id,
        pred_type: pred.pred_type,
        lag_hours: pred.lag_hours,
      };
      setRecoveryOps((prev) => withoutEdge(prev, edgeId));
      setRepoints((prev) => new Map(prev).set(edgeId, addOp));
      setDesc(`e${edgeId}`, {
        kind: "dep",
        label: `${row.task_code} ${row.task_name} ← ${external.taskCode}`,
        was: `${pred.pred_task_code} ${relLabel(orig.type, orig.lagHours)}`,
        now: `${external.taskCode} ${relLabel(orig.type, orig.lagHours)}`,
      });
    },
    [captureRel, setDesc],
  );

  const fetchExternals = React.useCallback(
    async (query: string): Promise<ExternalCandidate[]> => {
      const results = await searchActivities(scheduleId, query, 25);
      return results.map((r) => ({
        externalActivityId: r.id,
        taskCode: r.task_code,
        taskName: r.prefix || r.task_name,
        workCode: r.work_code,
        wbsName: r.section_name,
        startIso: r.start,
        finishIso: r.finish,
      }));
    },
    [scheduleId],
  );

  const stageDuration = React.useCallback(
    (row: WorkbenchRow, days: number) => {
      const id = row.activity_id;
      if (!originalDurations.current.has(id)) {
        originalDurations.current.set(id, row.duration_days);
      }
      const orig = originalDurations.current.get(id)!;
      setDurationEdits((prev) => {
        if (days === orig && !prev.has(id)) return prev; // no-op commit
        const next = new Map(prev);
        if (days === orig) next.delete(id);
        else next.set(id, days);
        return next;
      });
      setDesc(
        `d${id}`,
        days === orig
          ? null
          : {
              kind: "dur",
              label: `${row.task_code} ${row.task_name}`,
              was: `${orig}d`,
              now: `${days}d`,
            },
      );
    },
    [setDesc],
  );

  /** Anchor a floating source date by converting the gap into lag on the
   *  binding edge — staged as a normal dependency recovery edit, so it joins
   *  the batch, shows in the rail (unstageable), and the next preview makes
   *  the network agree with the seed (the advisory then clears itself). */
  const stageFloatAnchor = React.useCallback(() => {
    const f = result?.source_float;
    const src = result?.source;
    if (!f?.suggested || !src) return;
    const s = f.suggested;
    captureRel(s.edge_id, s.pred_type as PredType, s.lag_hours);
    setRecoveryOps((prev) => [
      ...withoutEdge(prev, s.edge_id),
      {
        op: "update",
        edge_id: s.edge_id,
        pred_type: s.pred_type,
        lag_hours: s.suggested_lag_hours,
      },
    ]);
    setDesc(`e${s.edge_id}`, {
      kind: "dep",
      label: `${src.task_code} ${src.task_name} ← ${s.pred_task_code}`,
      was: relLabel(s.pred_type as PredType, s.lag_hours),
      now: relLabel(s.pred_type as PredType, s.suggested_lag_hours),
    });
  }, [result, captureRel, setDesc]);

  const unstageRecovery = React.useCallback(
    (key: string) => {
      // The captured originals are kept — within a workbench session the DB
      // values can't change (preview rolls back; apply closes the modal), so
      // a re-edit after unstage still compares against the true original.
      if (key.startsWith("e")) {
        const edgeId = Number(key.slice(1));
        setRecoveryOps((prev) => withoutEdge(prev, edgeId));
        setRepoints((prev) => {
          if (!prev.has(edgeId)) return prev;
          const next = new Map(prev);
          next.delete(edgeId);
          return next;
        });
      } else {
        const actId = Number(key.slice(1));
        setDurationEdits((prev) => {
          const next = new Map(prev);
          next.delete(actId);
          return next;
        });
      }
      setDesc(key, null);
    },
    [setDesc],
  );

  // ---- apply ---------------------------------------------------------------

  const handleConfirmApply = React.useCallback(async (ignoreCascade = false) => {
    if (!onApply || applying) return;
    setApplying(true);
    setError(null);
    try {
      const body = buildBody();
      const applied = await onApply(
        ignoreCascade ? { ...body, ignore_cascade: true } : body
      );
      if (!applied.ok) {
        setResult(applied);
        setError(applied.error || "Some edits failed validation");
        setShowConfirm(false);
        return;
      }
      setShowConfirm(false);
      onApplied?.(applied);
    } catch (err) {
      setError(apiErrorMessage(err, "Apply failed"));
      setShowConfirm(false);
    } finally {
      setApplying(false);
    }
  }, [onApply, onApplied, applying, buildBody]);

  // ---- derived render state --------------------------------------------------

  const summary = result?.summary ?? null;
  const recoveryList = Array.from(recoveryDescs.entries());
  const recoveryCount = recoveryList.length;
  const initialChangeCount =
    (source && (source.new_start || source.new_end || source.actuals) ? 1 : 0) +
    initialLogicEdits.length;
  const totalChanges = initialChangeCount + recoveryCount;
  // Nothing to apply: no edits AND the pass moves nothing (e.g. F9 on an
  // already-reconciled schedule). Show "No changes" and a Close button.
  const nothingToApply =
    result != null && totalChanges === 0 && (summary?.impacted ?? 0) === 0;

  const impactedDelta =
    summary && baseline != null ? summary.impacted - baseline.impacted : 0;
  const cpRestored =
    summary != null && baseline?.cpImpact === true && !summary.critical_path_impact;

  const editedEdgeIds = React.useMemo(() => {
    const ids = new Set<number>();
    for (const op of recoveryOps) if ("edge_id" in op) ids.add(op.edge_id);
    // Re-points live in the separate `repoints` map (keyed by the old edge id),
    // so add them too or the re-pointed chip renders without the edited mark.
    Array.from(repoints.keys()).forEach((oldEdgeId) => ids.add(oldEdgeId));
    return ids;
  }, [recoveryOps, repoints]);
  const editedDurationIds = React.useMemo(
    () => new Set(durationEdits.keys()),
    [durationEdits],
  );
  const violatedIds = React.useMemo(
    () => new Set((result?.constraint_violations ?? []).map((v) => v.activity_id)),
    [result],
  );

  // Click the header "constraints violated" pill to jump to the offending row,
  // cycling through them on repeated clicks.
  const rowsRef = React.useRef<WorkbenchRowsHandle>(null);
  const [violationCursor, setViolationCursor] = React.useState(0);
  React.useEffect(() => setViolationCursor(0), [result]);
  const jumpToViolation = React.useCallback(() => {
    const ids = result?.constraint_violations.map((v) => v.activity_id) ?? [];
    if (ids.length === 0) return;
    rowsRef.current?.focusActivity(ids[violationCursor % ids.length]);
    setViolationCursor((c) => c + 1);
  }, [result, violationCursor]);

  const nameById = React.useMemo(() => {
    const map = new Map<number, string>();
    if (result) {
      if (result.source) map.set(result.source.activity_id, result.source.task_name);
      for (const row of result.rows) {
        map.set(row.activity_id, row.task_name);
        for (const p of row.preds) map.set(p.pred_activity_id, p.pred_task_name);
      }
    }
    return map;
  }, [result]);

  // Constraint violations are flagged inline on each row (the red ⚲* badge) and
  // counted in the header strip — they don't open the rail.
  const showRail =
    !!result &&
    (!!result.error ||
      result.logic_results.length > 0 ||
      result.actuals_summary != null ||
      recoveryCount > 0);

  const src = result?.source ?? null;
  const srcFloat = result?.source_float ?? null;
  const srcWc = src ? activityWorkCodeMap.get(src.activity_id) : undefined;
  const srcColor = srcWc ? colors[srcWc]?.fill : undefined;

  const saveDisabled =
    readOnly || previewing || applying || !result || !result.ok || !!error;

  return (
    <div className="cwb-overlay" onClick={applying ? undefined : onCancel}>
      <div
        className={`cwb-modal${showRail ? " cwb-modal--with-rail" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cwb-main">
          <header className="cwb-header">
            <div className="cwb-title-row">
              <div className="cwb-title">Schedule Change Preview</div>
              {!readOnly && (
                <span className={`cwb-edits-chip${recoveryCount ? " has-edits" : ""}`}>
                  {recoveryCount
                    ? `${recoveryCount} recovery edit${recoveryCount > 1 ? "s" : ""}`
                    : "no recovery edits"}
                </span>
              )}
              {previewing && result && (
                <span className="cwb-recalc">recalculating…</span>
              )}
              <div className="cwb-title-right">
                {!src && headerLabel && (
                  <span className="cwb-header-label">{headerLabel}</span>
                )}
                <button
                  type="button"
                  className="cwb-close"
                  onClick={onCancel}
                  disabled={applying}
                  aria-label="Close"
                  title="Close"
                >
                  ✕
                </button>
              </div>
            </div>

            {src ? (
              <div className="cwb-source-row">
                {src.is_critical && <span className="cp-badge">CP</span>}
                <span
                  className="cwb-swatch"
                  style={{ backgroundColor: srcColor || "#666" }}
                />
                {srcWc && <span className="cwb-wc">{srcWc}</span>}
                <span className="cwb-source-name">{src.task_name}</span>
                <span className="cwb-source-dates">
                  <DateDelta oldVal={src.old_start} newVal={src.new_start} />
                  <span className="cwb-date-sep">→</span>
                  <DateDelta oldVal={src.old_end} newVal={src.new_end} />
                </span>
                <span
                  className={`cwb-delta-chip${src.finish_delta < 0 ? " is-early" : ""}`}
                >
                  {formatSignedDays(src.finish_delta)}
                </span>
                {src.total_float !== null && (
                  <span className="tf-badge">TF: {src.total_float}</span>
                )}
              </div>
            ) : null}

            {srcFloat && !floatDismissed && (
              <div className="cwb-float-banner">
                <span className="cwb-float-text">
                  ⚓ Floating date — nothing drives{" "}
                  <b>{formatDateMonthDay(src?.new_start ?? null)}</b>; the next
                  reschedule would pull this back to{" "}
                  <b>{formatDateMonthDay(srcFloat.implied_start)}</b>.
                </span>
                {!readOnly && srcFloat.suggested && (
                  <button
                    type="button"
                    className="cwb-float-btn cwb-float-btn--anchor"
                    onClick={stageFloatAnchor}
                  >
                    Drive it with logic (
                    {relLabel(srcFloat.suggested.pred_type, srcFloat.suggested.suggested_lag_hours)} on{" "}
                    {srcFloat.suggested.pred_task_code})
                  </button>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    className="cwb-float-btn"
                    onClick={() => setFloatDismissed(true)}
                  >
                    Leave floating
                  </button>
                )}
              </div>
            )}

            {summary && summary.critical_path_impact && (
              <div className="cwb-cp-banner">
                <div className="cwb-cp-banner-title">
                  <span>⚠</span> CRITICAL PATH IMPACT <span>⚠</span>
                </div>
                <div className="cwb-cp-banner-sub">
                  This change affects {summary.cp_count} critical-path{" "}
                  {summary.cp_count === 1 ? "activity" : "activities"}
                  {!readOnly && " — edit durations or dependencies below to recover"}
                </div>
              </div>
            )}
            {cpRestored && (
              <div className="cwb-cp-banner cwb-cp-banner--ok">
                <div className="cwb-cp-banner-title">✓ Critical path restored</div>
                <div className="cwb-cp-banner-sub">
                  Your recovery edits absorbed the push — no critical-path impact
                </div>
              </div>
            )}

            {summary && (
              <div className="cwb-impact-strip">
                <span className={`cwb-impact-count${summary.impacted === 0 ? " is-ok" : ""}`}>
                  {summary.impacted === 0
                    ? baseline?.impacted
                      ? "0 dependent activities move — schedule recovered"
                      : "No dependent activities move"
                    : `${summary.impacted} dependent ${summary.impacted === 1 ? "activity" : "activities"} will move`}
                </span>
                {impactedDelta !== 0 && (
                  <span className={`cwb-impact-delta ${impactedDelta < 0 ? "is-down" : "is-up"}`}>
                    {impactedDelta < 0
                      ? `▼ ${-impactedDelta} recovered`
                      : `▲ ${impactedDelta} worse`}
                  </span>
                )}
                {result && result.constraint_violations.length > 0 && (
                  <button
                    type="button"
                    className="cwb-impact-violations"
                    title="Jump to the activity pushed past its date constraint (click again for the next)"
                    onClick={jumpToViolation}
                  >
                    ⚠ {result.constraint_violations.length} constraint{result.constraint_violations.length === 1 ? "" : "s"} violated
                  </button>
                )}
              </div>
            )}
          </header>

          <div className="cwb-body">
            {!result && (
              <div className="cwb-loading">
                {error ? error : "Simulating schedule impact…"}
              </div>
            )}
            {result && (
              <div className="cwb-rows-wrap">
                <Spinner visible={previewing} label="Recalculating…" />
                <WorkbenchRows
                  ref={rowsRef}
                  result={result}
                  colors={colors}
                  activityWorkCodeMap={activityWorkCodeMap}
                  sectionMap={sectionMap}
                  zoneMap={zoneMap}
                  readOnly={readOnly}
                  editedEdgeIds={editedEdgeIds}
                  editedDurationIds={editedDurationIds}
                  violatedIds={violatedIds}
                  onEdgeChange={stageEdgeChange}
                  onEdgeRemove={stageEdgeRemove}
                  onEdgeReplace={stageEdgeReplace}
                  onDurationChange={stageDuration}
                  fetchExternals={fetchExternals}
                />
              </div>
            )}
          </div>

          <footer className="cwb-actions">
            {error && result && <span className="cwb-error">{error}</span>}
            {!readOnly && (
              <span className="cwb-foot-note">
                {nothingToApply
                  ? replanAll
                    ? "Everything is up to date."
                    : "No changes — the schedule is already up to date."
                  : "Saves everything above as one batch — a single undo from the Changes tab."}
              </span>
            )}
            <button
              className="cwb-btn"
              onClick={onBack ?? onCancel}
              disabled={applying}
            >
              {readOnly ? "Close" : onBack ? "← Back" : nothingToApply ? "Close" : "Cancel"}
            </button>
            {/* Update Schedule flow: an explicit "leave the dates alone" exit.
                One button — "Done" (primary) when nothing moves, "Close
                without rescheduling" when declining the reschedule; the
                step-1 updates are already saved either way. */}
            {!readOnly && replanAll && (
              <button
                className={`cwb-btn${nothingToApply ? " cwb-btn--primary" : ""}`}
                onClick={onCancel}
                disabled={applying}
                title={nothingToApply
                  ? undefined
                  : "Your status updates are already saved. Close without moving any other dates — the header badge keeps flagging the schedule until you apply the new dates."}
              >
                {nothingToApply ? "Done" : "Close without rescheduling"}
              </button>
            )}
            {!readOnly && !replanAll && totalChanges > 0 && onApply && (
              <ActionCol
                btnClass="cwb-btn--warn"
                onClick={() => void handleConfirmApply(true)}
                disabled={saveDisabled}
                title="Write just your edits now — successors keep their dates. The badge will flag the schedule until you run Update Schedule."
                note="No cascade — Update Schedule reconciles"
              >
                {applying ? "Applying…" : "Apply Edits Only"}
              </ActionCol>
            )}
            {!readOnly && !nothingToApply && (
              <ActionCol
                btnClass="cwb-btn--primary"
                onClick={() => setShowConfirm(true)}
                disabled={saveDisabled}
                title={error ? "Fix the errors before saving" : undefined}
                note={replanAll ? "Accepts the new dates above" : "Cascades successors — keeps the schedule current"}
              >
                {applying
                  ? "Saving…"
                  : replanAll && totalChanges === 0
                  ? `Apply update — ${summary?.impacted ?? 0} ${
                      (summary?.impacted ?? 0) === 1 ? "activity moves" : "activities move"
                    }`
                  : `Save All (${totalChanges} change${totalChanges === 1 ? "" : "s"}, ${summary?.impacted ?? 0} cascaded)`}
              </ActionCol>
            )}
          </footer>
        </div>

        {showRail && result && (
          <WorkbenchRail
            result={result}
            nameById={nameById}
            activityWorkCodeMap={activityWorkCodeMap}
            colors={colors}
            recovery={recoveryList}
            onUnstage={readOnly ? undefined : unstageRecovery}
          />
        )}
      </div>

      {showConfirm && result && (
        <ConfirmSummaryModal
          result={result}
          headerLabel={headerLabel}
          initialLogicEditCount={initialLogicEdits.length}
          recovery={recoveryList.map(([, d]) => d)}
          initialImpacted={baseline?.impacted ?? null}
          applying={applying}
          onBack={() => setShowConfirm(false)}
          onConfirm={() => void handleConfirmApply()}
        />
      )}
    </div>
  );
};

export default CascadeWorkbenchModal;
